/**
 * Filtre et adapte les recommandations de travaux en fonction :
 * - Des données DPE réelles (NORMALISÉES)
 * - Des réponses de l'utilisateur aux questions
 *
 * VERSION 2 - COMPATIBLE LEGACY
 * Gère les champs undefined pour les DPE legacy
 */

import { 
  NormalizedDPEData, 
  isChauffageElectrique,
  isChauffageElectriqueRadiateurs,
  isAppartement,
  isChauffageGaz,
  isECSElectrique
} from './dpe-data-normalizer';

import {
  parseNombreHabitants,
  getAnswerByQuestionId,
  getSelectedValue,
  isSystemRecent,
  parseVolumeECS
} from './answer-parsers';

interface UserAnswer {
  question_id: string;
  selected_option?: string;
  selected_option_id?: string;
  selected_options?: string[];
}

interface UserAnswers {
  [questionId: string]: any;
}

/**
 * Convertit le tableau d'answers en objet plat pour faciliter l'accès
 */
export function normalizeAnswers(answers: UserAnswer[] | UserAnswers): UserAnswers {
  if (!Array.isArray(answers)) {
    return answers;
  }

  const normalized: UserAnswers = {};
  for (const answer of answers) {
    normalized[answer.question_id] = answer.selected_option || answer.selected_option_id || answer.selected_options;
  }
  return normalized;
}

interface WorkRecommendation {
  category: 'windows' | 'wall_insulation' | 'roof_insulation' | 'floor_insulation' | 'heating' | 'hot_water' | 'ventilation' | 'heating_control';
  priority: 'high' | 'medium' | 'low';
  reason: string;
  estimatedSavings?: number;
  isFeasible: boolean;
  userConfirmed?: boolean;
  blockers?: string[];
}

/**
 * Analyse si les travaux de fenêtres sont pertinents
 */
function analyzeWindowsRecommendation(dpe: NormalizedDPEData, answers: UserAnswers): WorkRecommendation | null {
  const blockers: string[] = [];

  // Vérifier qualité des fenêtres
  const hasOldWindows = dpe.windows_type === 'insuffisante' || 
                        dpe.windows_type === 'moyenne' ||
                        dpe.qualite_isolation_menuiseries === 'insuffisante' ||
                        dpe.qualite_isolation_menuiseries === 'moyenne';

  // ✅ GESTION undefined pour DPE legacy
  const hasSignificantLoss = (dpe.deperditions_baies_vitrees ?? 0) > 3;

  if (!hasOldWindows || !hasSignificantLoss) {
    return null;
  }

  if (answers.isolation_menuiseries === 'recent' || answers.windows_recently_changed === true) {
    return null;
  }

  if (answers.building_constraints?.includes('copropriete_interdit_changement_fenetres')) {
    blockers.push('Copropriété interdit le changement de fenêtres');
  }

  if (answers.fenestres_monument_historique === true) {
    blockers.push('Bâtiment classé monument historique');
  }

  const isFeasible = blockers.length === 0;

  return {
    category: 'windows',
    priority: hasSignificantLoss && (dpe.deperditions_baies_vitrees ?? 0) > 5 ? 'high' : 'medium',
    reason: `Fenêtres ${dpe.windows_type || 'anciennes'} avec déperditions${dpe.deperditions_baies_vitrees ? ` de ${dpe.deperditions_baies_vitrees} W/K` : ' importantes'}`,
    estimatedSavings: Math.round((dpe.deperditions_baies_vitrees ?? 20) * 25),
    isFeasible,
    userConfirmed: answers.confirm_windows_upgrade === true,
    blockers: blockers.length > 0 ? blockers : undefined
  };
}

/**
 * Analyse si l'isolation des murs est pertinente
 */
function analyzeWallInsulationRecommendation(dpe: NormalizedDPEData, answers: UserAnswers): WorkRecommendation | null {
  const blockers: string[] = [];

  const hasPoorInsulation = dpe.wall_insulation === 'insuffisante' ||
                           dpe.wall_insulation === 'moyenne' ||
                           dpe.qualite_isolation_murs === 'insuffisante' ||
                           dpe.qualite_isolation_murs === 'moyenne';

  // ✅ GESTION undefined - seuil abaissé à 2 W/K et on accepte aussi si déperditions inconnues mais isolation mauvaise
  const deperditionsMurs = dpe.deperditions_murs ?? null;
  const hasSignificantLoss = deperditionsMurs !== null ? deperditionsMurs > 2 : hasPoorInsulation;

  // ✅ Si ni l'isolation ni les déperditions ne signalent de problème, skip
  if (!hasPoorInsulation && !hasSignificantLoss) {
    return null;
  }

  if (answers.isolation_murs_3 === 'isolation_recente') {
    return null;
  }

  const iteImpossible = answers.urbanisme_ite_6 === 'ite_interdite';
  const itiDifficile = answers.faisabilite_iti_7 === 'iti_difficile';

  // ✅ ITE+ITI impossibles: on garde la recommandation avec un avertissement
  // plutôt que de la supprimer silencieusement

  const warnings: string[] = [];

  if (answers.type_murs === 'murs_mitoyens_majoritaires') {
    warnings.push('Majorité de murs mitoyens (surface isolable réduite)');
  }

  if (iteImpossible) {
    blockers.push('Isolation extérieure impossible (contraintes urbanisme/PLU)');
  }

  if (answers.facade_monument_historique === true) {
    blockers.push('Façade classée monument historique');
  }

  const isFeasible = !(iteImpossible && itiDifficile);

  if (iteImpossible && itiDifficile) {
    blockers.push('ITI difficile à réaliser (perte de surface, contraintes techniques)');
  }

  return {
    category: 'wall_insulation',
    priority: dpe.wall_insulation === 'insuffisante' || (dpe.deperditions_murs ?? 0) > 5 ? 'high' : 'medium',
    reason: `Isolation murs ${dpe.wall_insulation || 'insuffisante'}${dpe.deperditions_murs ? ` avec déperditions de ${dpe.deperditions_murs} W/K` : ''}`,
    estimatedSavings: Math.round((dpe.deperditions_murs ?? 30) * 55),
    isFeasible,
    userConfirmed: answers.confirm_wall_insulation === true,
    blockers: [...blockers, ...warnings].length > 0 ? [...blockers, ...warnings] : undefined
  };
}

/**
 * Analyse si l'isolation de la toiture est pertinente
 */
function analyzeRoofInsulationRecommendation(dpe: NormalizedDPEData, answers: UserAnswers): WorkRecommendation | null {
  const blockers: string[] = [];

  const hasPoorInsulation = dpe.roof_insulation === 'insuffisante' ||
                           dpe.roof_insulation === 'moyenne' ||
                           dpe.qualite_isolation_plancher_haut_comble_amenage === 'insuffisante' ||
                           dpe.qualite_isolation_plancher_haut_comble_perdu === 'insuffisante';

  // ✅ GESTION undefined
  const hasSignificantLoss = ((dpe.deperditions_planchers_hauts ?? dpe.deperditions_toiture) ?? 0) > 4;

  if (!hasPoorInsulation || !hasSignificantLoss) {
    return null;
  }

  if (answers.toiture_isolee_recemment === true) {
    return null;
  }

  // Vérifier si toiture applicable
  const isTopFloorCombles = answers.dernier_etage === 'dernier_etage_sous_combles';
  const isTopFloorFlatRoof = answers.dernier_etage === 'dernier_etage_sous_toit_terrasse';
  const isHouseWithAttic = answers.type_toiture_maison === 'combles_amenages' || answers.type_toiture_maison === 'combles_perdus';
  const isHouseFlatRoof = answers.type_toiture_maison === 'toit_terrasse';

  // Si pas de toiture applicable, bloquer la recommandation
  if (!isTopFloorCombles && !isTopFloorFlatRoof && !isHouseWithAttic && !isHouseFlatRoof) {
    return null;
  }

  const isFlatRoof = isTopFloorFlatRoof || isHouseFlatRoof;
  const faisabiliteRampants = answers.isolation_toiture_faisabilite_rampants;

  // Pour combles, vérifier la faisabilité
  if (!isFlatRoof && faisabiliteRampants === 'difficilement_envisageable') {
    return null;
  }

  const isFeasible = blockers.length === 0;

  return {
    category: 'roof_insulation',
    priority: hasSignificantLoss && ((dpe.deperditions_planchers_hauts ?? 0) > 6) ? 'high' : 'medium',
    reason: `Isolation toiture ${dpe.roof_insulation || 'insuffisante'} avec déperditions importantes`,
    estimatedSavings: Math.round(((dpe.deperditions_planchers_hauts ?? dpe.deperditions_toiture) ?? 25) * 30),
    isFeasible,
    userConfirmed: answers.confirm_roof_insulation === true,
    blockers: blockers.length > 0 ? blockers : undefined
  };
}

/**
 * Analyse si le changement de chauffage est pertinent
 *
 * NOTE: Si le chauffage est collectif, cette fonction retourne null
 * pour filtrer complètement la recommandation. Un warning dédié est
 * affiché séparément pour informer l'utilisateur.
 */
function analyzeHeatingSystemRecommendation(dpe: NormalizedDPEData, answers: UserAnswers): WorkRecommendation | null {
  const blockers: string[] = [];

  const isElectricRadiators = isChauffageElectriqueRadiateurs(dpe);
  const isGas = isChauffageGaz(dpe);

  if (answers.chauffage_change_recemment === true) {
    return null;
  }

  // ✅ FILTRAGE SYSTÈME COLLECTIF: Retourner null pour filtrer complètement
  // la recommandation au lieu de la marquer comme non-faisable.
  // Un warning dédié est affiché dans les résultats pour expliquer la situation.
  if (answers.chauffage_collectif === true || 
      answers.chauffage_collectif === 'true' ||
      answers.chauffage_collectif === 'chauffage_collectif_oui') {
    console.log('🚫 Filtering heating recommendation: collective heating system detected');
    return null;
  }

  if (answers.impossibilite_pac_exterieur === true) {
    blockers.push('Impossibilité d\'installer une PAC (unité extérieure interdite)');
  }

  // CAS 1 : Chauffage électrique direct
  if (isElectricRadiators) {
    const isFeasible = blockers.length === 0;

    return {
      category: 'heating',
      priority: 'high',
      reason: 'Chauffage électrique direct très pénalisant. Passage à PAC air/eau ou air/air recommandé (attention : ne pas proposer gaz car dégrade le GES)',
      estimatedSavings: 2000,
      isFeasible,
      userConfirmed: answers.confirm_heating_change === true,
      blockers: blockers.length > 0 ? blockers : undefined
    };
  }

  // CAS 2 : Chaudière gaz
  if (isGas) {
    const isRecent = isSystemRecent(answers.annee_installation_chauffage, 15);

    if (isRecent) {
      return null;
    }

    const isFeasible = blockers.length === 0;
    
    const anneeInstallation = answers.annee_installation_chauffage
      ? parseInt(answers.annee_installation_chauffage)
      : null;
    const currentYear = new Date().getFullYear();
    const age = anneeInstallation ? currentYear - anneeInstallation : null;

    return {
      category: 'heating',
      priority: 'medium',
      reason: age
        ? `Chaudière gaz ancienne (${age} ans). Remplacement par PAC recommandé (ou radiateurs électriques si PAC impossible) pour améliorer efficacité et réduire les GES`
        : 'Chaudière gaz à remplacer par PAC (ou radiateurs électriques si PAC impossible) pour améliorer efficacité et réduire les GES',
      estimatedSavings: 1000,
      isFeasible,
      userConfirmed: answers.confirm_heating_change === true,
      blockers: blockers.length > 0 ? blockers : undefined
    };
  }

  // CAS 3 : Autres systèmes anciens
  const isRecent = isSystemRecent(answers.annee_installation_chauffage, 20);
  
  if (isRecent) {
    return null;
  }

  const isFeasible = blockers.length === 0;

  return {
    category: 'heating',
    priority: 'low',
    reason: 'Système de chauffage ancien à optimiser',
    estimatedSavings: 500,
    isFeasible,
    userConfirmed: answers.confirm_heating_change === true,
    blockers: blockers.length > 0 ? blockers : undefined
  };
}

/**
 * Analyse si le changement d'ECS est pertinent
 *
 * NOTE: Si l'ECS est collectif, cette fonction retourne null
 * pour filtrer complètement la recommandation. Un warning dédié est
 * affiché séparément pour informer l'utilisateur.
 */
function analyzeHotWaterSystemRecommendation(dpe: NormalizedDPEData, answers: UserAnswers): WorkRecommendation | null {
  const blockers: string[] = [];

  const hasElectricHotWater = isECSElectrique(dpe);

  if (!hasElectricHotWater) {
    return null;
  }

  if (answers.ballon_ecs_change_recemment === true) {
    return null;
  }

  if (isSystemRecent(answers.annee_installation_ecs, 10)) {
    return null;
  }

  // ✅ FILTRAGE SYSTÈME COLLECTIF: Retourner null pour filtrer complètement
  // la recommandation au lieu de la marquer comme non-faisable.
  // Un warning dédié est affiché dans les résultats pour expliquer la situation.
  if (answers.ecs_collectif === true || 
      answers.ecs_collectif === 'true' ||
      answers.ecs_collectif === 'ecs_collectif_oui') {
    console.log('🚫 Filtering hot water recommendation: collective ECS detected');
    return null;
  }

  const isFeasible = blockers.length === 0;

  const nbHabitants = parseNombreHabitants(answers.nombre_habitants);
  
  const hotWaterType = dpe.hot_water_type?.toLowerCase() || '';
  const isAccumulationTank = hotWaterType.includes('accumulation') || hotWaterType.includes('ballon');

  const currentVolume = parseVolumeECS(hotWaterType);

  let reason = 'ECS électrique à optimiser';
  let priority: 'high' | 'medium' | 'low' = 'low';

  if (isAccumulationTank) {
    reason = 'Ballon électrique à accumulation détecté. Passage au ballon instantané recommandé pour réduire les pertes thermiques';
    priority = 'medium';
  }

  if (currentVolume) {
    if (nbHabitants === 1 && currentVolume > 80) {
      reason = `Ballon surdimensionné (${currentVolume}L pour 1 habitant). Réduction à max 80L recommandée pour économiser l'énergie`;
      priority = 'medium';
    } else if (nbHabitants === 2 && (currentVolume < 70 || currentVolume > 100)) {
      if (currentVolume < 70 && answers.ecs_suffisante === false) {
        reason = `Ballon sous-dimensionné (${currentVolume}L pour 2 habitants). Augmentation à 70-100L recommandée`;
        priority = 'high';
      } else if (currentVolume > 100) {
        reason = `Ballon surdimensionné (${currentVolume}L pour 2 habitants). Réduction à 70-100L recommandée`;
        priority = 'medium';
      }
    } else if (nbHabitants === 3 && (currentVolume < 100 || currentVolume > 150)) {
      if (currentVolume < 100 && answers.ecs_suffisante === false) {
        reason = `Ballon sous-dimensionné (${currentVolume}L pour 3 habitants). Augmentation à 100-150L recommandée`;
        priority = 'high';
      } else if (currentVolume > 150) {
        reason = `Ballon surdimensionné (${currentVolume}L pour 3 habitants). Réduction à 100-150L recommandée`;
        priority = 'medium';
      }
    } else if (nbHabitants >= 4) {
      if (currentVolume < 150 && answers.ecs_suffisante === false) {
        reason = `Ballon sous-dimensionné (${currentVolume}L pour ${nbHabitants} habitants). Augmentation à min 150L recommandée`;
        priority = 'high';
      }
    }
  }

  return {
    category: 'hot_water',
    priority,
    reason,
    estimatedSavings: priority === 'high' ? 500 : 300,
    isFeasible,
    userConfirmed: answers.confirm_ecs_change === true,
    blockers: blockers.length > 0 ? blockers : undefined
  };
}

/**
 * Analyse si l'installation ou le remplacement de ventilation est pertinent
 */
function analyzeVentilationRecommendation(dpe: NormalizedDPEData, answers: UserAnswers): WorkRecommendation | null {
  const ventilationAnswer = answers.ventilation_etat_2;
  const ventilationLoss = dpe.deperditions_renouvellement_air;

  const hasNaturalVentilation =
    dpe.ventilation_system?.toLowerCase().includes('naturel') ||
    dpe.ventilation_system?.toLowerCase().includes('fenêtre') ||
    dpe.ventilation_system?.toLowerCase().includes('ouverture');

  const needsVentilation =
    ventilationAnswer === 'vmc_absente' ||
    ventilationAnswer === 'vmc_defaillante' ||
    hasNaturalVentilation;

  if (!needsVentilation) {
    return null;
  }

  if (ventilationAnswer === 'vmc_fonctionnelle') {
    return null;
  }

  const hasSignificantLoss = ventilationLoss && ventilationLoss > 3;

  let priority: 'high' | 'medium' | 'low' = 'medium';
  if (ventilationAnswer === 'vmc_absente' || ventilationAnswer === 'vmc_defaillante') {
    priority = 'high';
  }

  let reason = 'Améliorer la qualité de l\'air et réduire les déperditions par renouvellement d\'air';
  if (ventilationAnswer === 'vmc_absente') {
    reason = 'Absence de VMC : installation recommandée pour la qualité de l\'air et les économies d\'énergie';
  } else if (ventilationAnswer === 'vmc_defaillante') {
    reason = 'VMC défaillante : remplacement nécessaire pour optimiser la ventilation';
  } else if (hasNaturalVentilation) {
    reason = 'Ventilation naturelle : passage à VMC recommandé pour contrôler les déperditions';
  }

  return {
    category: 'ventilation',
    priority,
    reason,
    estimatedSavings: hasSignificantLoss ? Math.round(ventilationLoss * 20) : 15,
    isFeasible: true,
    userConfirmed: ventilationAnswer === 'vmc_absente' || ventilationAnswer === 'vmc_defaillante',
    blockers: undefined
  };
}

/**
 * Analyse si l'isolation du plancher bas est pertinente
 */
function analyzeFloorInsulationRecommendation(dpe: NormalizedDPEData, answers: UserAnswers): WorkRecommendation | null {
  const hasPoorInsulation =
    dpe.floor_insulation === 'insuffisante' ||
    dpe.floor_insulation === 'moyenne' ||
    dpe.qualite_isolation_plancher_bas === 'insuffisante' ||
    dpe.qualite_isolation_plancher_bas === 'moyenne';

  const hasSignificantLoss = (dpe.deperditions_planchers_bas ?? 0) > 4;

  if (!hasPoorInsulation || !hasSignificantLoss) {
    return null;
  }

  const plancherAnswer = answers.plancher_clarification_9;
  const dernierEtageAnswer = answers.dernier_etage;

  const isCaveOrVideSanitaire =
    plancherAnswer === 'plancher_cave' ||
    plancherAnswer === 'plancher_vide_sanitaire';

  const isRDCCave = dernierEtageAnswer === 'dernier_etage_rdc_cave';

  const isFeasible = isCaveOrVideSanitaire || isRDCCave;

  const blockers: string[] = [];

  if (plancherAnswer === 'plancher_terre_plein') {
    blockers.push('Isolation par le dessus requise (terre-plein) - intervention plus complexe');
  } else if (plancherAnswer === 'plancher_local_chauffe') {
    blockers.push('Plancher sur local chauffé - impact limité');
  }

  let reason = 'Isolation plancher bas insuffisante avec déperditions importantes';
  if (isCaveOrVideSanitaire) {
    reason = 'Isolation du plafond de cave/vide sanitaire recommandée - accès facile, impact significatif';
  } else if (isRDCCave) {
    reason = 'RDC sur cave : isolation du plafond recommandée pour réduire les déperditions par le sol';
  }

  const priority: 'high' | 'medium' | 'low' =
    hasSignificantLoss && ((dpe.deperditions_planchers_bas ?? 0) > 6) ? 'high' : 'medium';

  return {
    category: 'floor_insulation',
    priority,
    reason,
    estimatedSavings: Math.round(((dpe.deperditions_planchers_bas ?? 0) * 25)),
    isFeasible,
    userConfirmed: isCaveOrVideSanitaire || isRDCCave,
    blockers: blockers.length > 0 ? blockers : undefined
  };
}

/**
 * Analyse si l'ajout d'une régulation programmable est pertinent
 */
function analyzeHeatingControlRecommendation(dpe: NormalizedDPEData, answers: UserAnswers): WorkRecommendation | null {
  const regulationAnswer = answers.regulation_programmable;

  if (!regulationAnswer) {
    return null;
  }

  if (regulationAnswer === 'regulation_equipped') {
    return null;
  }

  if (regulationAnswer !== 'regulation_not_equipped' && regulationAnswer !== 'regulation_partial') {
    return null;
  }

  const heatingConsumption = dpe.conso_chauffage_ef ?? dpe.besoin_chauffage;
  if (!heatingConsumption || heatingConsumption === 0) {
    return null;
  }

  const estimatedSavings = Math.round(heatingConsumption * 0.06 * 0.2276);

  const reason = regulationAnswer === 'regulation_partial'
    ? 'Régulation partielle ou insuffisante. Une régulation complète permettrait d\'atteindre le gain standard de 6% reconnu par la méthode DPE. Les 3 niveaux proposés offrent le même gain énergétique : le choix dépend du confort souhaité et du budget.'
    : 'Absence de régulation programmable. L\'installation d\'une régulation permet un gain de 6% sur la consommation de chauffage (valeur de référence méthode DPE). Les 3 niveaux proposés (programmable, connecté, par pièce) offrent le même gain énergétique calculé.';

  return {
    category: 'heating_control',
    priority: 'medium',
    reason,
    estimatedSavings,
    isFeasible: true,
    userConfirmed: true,
    blockers: undefined
  };
}

/**
 * Génère la liste complète des recommandations filtrées
 */
export function generateFilteredRecommendations(
  dpe: NormalizedDPEData,
  answersInput: UserAnswer[] | UserAnswers
): WorkRecommendation[] {
  const recommendations: WorkRecommendation[] = [];

  const answers = normalizeAnswers(answersInput);

  const windowsRec = analyzeWindowsRecommendation(dpe, answers);
  if (windowsRec) recommendations.push(windowsRec);

  const wallsRec = analyzeWallInsulationRecommendation(dpe, answers);
  if (wallsRec) recommendations.push(wallsRec);

  const roofRec = analyzeRoofInsulationRecommendation(dpe, answers);
  if (roofRec) recommendations.push(roofRec);

  const heatingRec = analyzeHeatingSystemRecommendation(dpe, answers);
  if (heatingRec) recommendations.push(heatingRec);

  const hotWaterRec = analyzeHotWaterSystemRecommendation(dpe, answers);
  if (hotWaterRec) recommendations.push(hotWaterRec);

  const ventilationRec = analyzeVentilationRecommendation(dpe, answers);
  if (ventilationRec) recommendations.push(ventilationRec);

  const floorRec = analyzeFloorInsulationRecommendation(dpe, answers);
  if (floorRec) recommendations.push(floorRec);

  const heatingControlRec = analyzeHeatingControlRecommendation(dpe, answers);
  if (heatingControlRec) recommendations.push(heatingControlRec);

  return recommendations.sort((a, b) => {
    if (a.priority !== b.priority) {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    }

    if (a.isFeasible !== b.isFeasible) {
      return a.isFeasible ? -1 : 1;
    }

    if (a.userConfirmed !== b.userConfirmed) {
      return a.userConfirmed ? -1 : 1;
    }

    return (b.estimatedSavings || 0) - (a.estimatedSavings || 0);
  });
}

/**
 * Vérifie si un type de travaux est applicable
 */
export function isWorkTypeApplicable(
  workType: string,
  dpe: NormalizedDPEData,
  answersInput: UserAnswer[] | UserAnswers
): boolean {
  const recommendations = generateFilteredRecommendations(dpe, answersInput);
  return recommendations.some(rec => rec.category === workType && rec.isFeasible);
}

// ============================================================================
// FILTRAGE DES ÉQUIPEMENTS (reste identique)
// ============================================================================

type EquipmentCategory = 'windows' | 'wall_insulation' | 'roof_insulation' | 'floor_insulation' | 'heating' | 'hot_water' | 'ventilation' | 'heating_control';

interface EquipmentStandard {
  id: string;
  category: EquipmentCategory;
  name: string;
  description: string | null;
  u_value?: number | null;
  r_value?: number | null;
  efficiency?: number | null;
  cop?: number | null;
  thickness_mm?: number | null;
  volume_liters?: number | null;
  power_kw?: number | null;
  min_construction_year?: number | null;
  max_construction_year?: number | null;
  energy_type?: string | null;
  is_default: boolean;
  display_order: number;
  is_active: boolean;
  insulation_location?: string | null;
  material_type?: string | null;
  is_breathable?: boolean | null;
  created_at: string;
  updated_at: string;
}

export function filterHeatingEquipments(
  equipments: EquipmentStandard[],
  dpe: NormalizedDPEData,
  answers: UserAnswers
): EquipmentStandard[] {
    // Si chauffage collectif, retourner liste vide
  if (answers.chauffage_collectif === true || 
      answers.chauffage_collectif === 'true' ||
      answers.chauffage_collectif === 'chauffage_collectif_oui') {
    console.log('🚫 Filtering heating equipments: collective heating detected, returning empty list');
    return [];
  }

  const isElectricRadiators = isChauffageElectriqueRadiateurs(dpe);
  const isGas = isChauffageGaz(dpe);
  const isApt = isAppartement(dpe);
  const heatingEnergyRaw = (dpe.heating_energy || '').toLowerCase();
  const isFioul = heatingEnergyRaw.includes('fioul') || heatingEnergyRaw.includes('fuel');

  const isOldGasBoiler = isGas && !isSystemRecent(answers.annee_installation_chauffage, 15);

  return equipments.filter(equipment => {
    const equipmentName = equipment.name.toLowerCase();
    const equipmentDescription = (equipment.description || '').toLowerCase();
    const fullText = equipmentName + ' ' + equipmentDescription;

    // PAC air-air : visible même avec un réseau hydraulique existant (gaz/fioul).
    // L'utilisateur peut choisir de remplacer ses radiateurs à eau par des
    // émetteurs air-air. Un avertissement est affiché dans l'UI (EquipmentSelector).

    if (isElectricRadiators) {
      if (equipmentName.includes('radiateur') && equipmentName.includes('électrique')) {
        return false;
      }
      if (equipmentName.includes('radiateur') && equipment.energy_type === 'electricite') {
        return false;
      }
      if (equipmentName.includes('convecteur')) {
        return false;
      }
      if (equipmentDescription.includes('convecteur')) {
        return false;
      }
    }

    if (isApt) {
      if (fullText.includes('bois') || fullText.includes('granule') || fullText.includes('granulé')) {
        return false;
      }
    }

    if (isElectricRadiators && fullText.includes('gaz')) {
      return false;
    }

    if (isOldGasBoiler && fullText.includes('gaz')) {
      return false;
    }

    if (!isGas && fullText.includes('gaz')) {
      return false;
    }

    return true;
  });
}

export function filterHotWaterEquipments(
  equipments: EquipmentStandard[],
  dpe: NormalizedDPEData,
  answers: UserAnswers
): EquipmentStandard[] {
  // Si ECS collectif, retourner liste vide
  if (answers.ecs_collectif === true || 
      answers.ecs_collectif === 'true' ||
      answers.ecs_collectif === 'ecs_collectif_oui') {
    console.log('🚫 Filtering hot water equipments: collective ECS detected, returning empty list');
    return [];
  }

  const nbHabitants = parseNombreHabitants(answers.nombre_habitants);

  const hotWaterType = dpe.hot_water_type?.toLowerCase() || '';
  const isAccumulationTank = hotWaterType.includes('accumulation') || hotWaterType.includes('ballon');
  const hasGasHotWater = dpe.hot_water_energy?.toLowerCase().includes('gaz');

  const currentVolume = parseVolumeECS(hotWaterType);

  let recommendedVolumeMin = 0;
  let recommendedVolumeMax = 0;
  let volumeDescription = '';

  if (nbHabitants === 1) {
    recommendedVolumeMin = 60;
    recommendedVolumeMax = 70;
    volumeDescription = '60 à 70 litres';
  } else if (nbHabitants === 2) {
    recommendedVolumeMin = 70;
    recommendedVolumeMax = 90;
    volumeDescription = '70 à 90 litres';
  } else if (nbHabitants === 3) {
    recommendedVolumeMin = 100;
    recommendedVolumeMax = 100;
    volumeDescription = '100 litres';
  } else if (nbHabitants >= 4) {
    recommendedVolumeMin = 150;
    recommendedVolumeMax = 150;
    volumeDescription = '150 litres';
  }

  const isOversized = currentVolume && currentVolume > recommendedVolumeMax + 30;

  if (isOversized && isAccumulationTank) {
    const optimalVolume = Math.round((recommendedVolumeMin + recommendedVolumeMax) / 2);
    const habitantText = nbHabitants === 1 ? '1 habitant' : `${nbHabitants} habitants`;

    return [{
      id: `semi-instantaneous-${optimalVolume}`,
      category: 'hot_water',
      name: `Chauffe-eau semi-instantané ${optimalVolume}L`,
      description: `Chauffe-eau semi-instantané adapté à ${habitantText}. Volume optimal : ${volumeDescription}. Réduit les pertes thermiques par rapport à votre ballon actuel de ${currentVolume}L.`,
      efficiency: 95,
      cop: null,
      volume_liters: optimalVolume,
      energy_type: 'electricite',
      display_order: 1,
      is_default: true,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }];
  }

  let filteredEquipments = equipments.filter(equipment => {
    const equipmentName = equipment.name.toLowerCase();

    if (!hasGasHotWater && equipmentName.includes('gaz')) {
      return false;
    }

    return true;
  });

  filteredEquipments = filteredEquipments.filter(equipment => {
    const volumeMatch = equipment.name.match(/(\d+)\s*l/i);
    if (!volumeMatch) return true;

    const volume = parseInt(volumeMatch[1]);
    return volume >= recommendedVolumeMin && volume <= recommendedVolumeMax;
  });

  // Ne garder que les volumes standards pour simplifier le choix utilisateur
  const standardVolumes = [65, 80, 100, 150];
  filteredEquipments = filteredEquipments.filter(equipment => {
    const volumeMatch = equipment.name.match(/(\d+)\s*l/i);
    if (!volumeMatch) return true; // Garder les équipements sans volume spécifié

    const volume = parseInt(volumeMatch[1]);
    return standardVolumes.includes(volume);
  });

  if (filteredEquipments.length === 0) {
    const optimalVolume = Math.round((recommendedVolumeMin + recommendedVolumeMax) / 2);
    const habitantText = nbHabitants === 1 ? '1 habitant' : `${nbHabitants} habitants`;

    return [{
      id: `semi-instantaneous-${optimalVolume}`,
      category: 'hot_water',
      name: `Chauffe-eau semi-instantané ${optimalVolume}L`,
      description: `Chauffe-eau semi-instantané adapté à ${habitantText}. Volume optimal : ${volumeDescription}.`,
      efficiency: 95,
      cop: null,
      volume_liters: optimalVolume,
      energy_type: 'electricite',
      display_order: 1,
      is_default: true,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }];
  }

  return filteredEquipments;
}

export function filterWindowsEquipments(
  equipments: EquipmentStandard[],
  dpe: NormalizedDPEData,
  answers: UserAnswers
): EquipmentStandard[] {
  return equipments.filter(equipment => {
    const equipmentName = equipment.name.toLowerCase();

    if (equipmentName.includes('simple vitrage') || equipmentName.includes('double vitrage ancien')) {
      return false;
    }

    return true;
  });
}

/**
 * Détermine si un matériau perspirant est requis selon la nature des murs
 *
 * @param wallType - Type de mur de l'utilisateur (nature_murs)
 * @returns true si seuls les matériaux perspirants doivent être proposés
 */
export function requiresBreathableMaterial(wallType: string | undefined): boolean {
  if (!wallType) {
    console.log('🔍 [BREATHABLE FILTER] No wall type specified, defaulting to breathable materials only (safety first)');
    return true;
  }

  const needsBreathable = [
    'pierre',
    'stone',
    'brique_pleine',
    'solid_brick',
    'ne_sais_pas',
    'unknown'
  ];

  const result = needsBreathable.includes(wallType);

  console.log(`🔍 [BREATHABLE FILTER] Wall type: ${wallType}, requires breathable: ${result}`);

  return result;
}

/**
 * Vérifie la compatibilité d'un matériau avec le type de mur
 *
 * @param equipment - Équipement d'isolation
 * @param wallType - Type de mur de l'utilisateur
 * @returns Objet avec niveau d'alerte et message si incompatibilité détectée
 */
export function checkWallMaterialCompatibility(
  equipment: EquipmentStandard & { is_breathable?: boolean | null },
  wallType: string | undefined
): { level: 'warning' | 'danger' | null; message: string | null } {
  if (equipment.category !== 'wall_insulation') {
    return { level: null, message: null };
  }

  const wallNeedsBreathable = requiresBreathableMaterial(wallType);
  const materialIsBreathable = equipment.is_breathable === true;
  const materialIsNotBreathable = equipment.is_breathable === false;

  if (wallNeedsBreathable && materialIsNotBreathable) {
    const wallTypeLabel = wallType === 'pierre' || wallType === 'stone'
      ? 'murs en pierre'
      : wallType === 'brique_pleine' || wallType === 'solid_brick'
      ? 'murs en brique pleine ancienne'
      : 'ce type de murs';

    return {
      level: 'danger',
      message: `⚠️ ATTENTION : Ce matériau non perspirant est incompatible avec des ${wallTypeLabel}. Risque grave d'humidité et de moisissures. Privilégiez un matériau perspirant (laine minérale, fibre de bois, liège).`
    };
  }

  if (!wallNeedsBreathable && materialIsBreathable) {
    return {
      level: null,
      message: 'ℹ️ Matériau perspirant - Compatible avec tous types de murs, y compris pierre et brique anciennes.'
    };
  }

  return { level: null, message: null };
}

/**
 * Filtre les équipements d'isolation des murs selon :
 * 1. Contraintes ITE/ITI (urbanisme, faisabilité)
 * 2. Perméabilité des matériaux (breathability)
 */
export function filterWallInsulationEquipments(
  equipments: EquipmentStandard[],
  dpe: NormalizedDPEData,
  answers: UserAnswers
): EquipmentStandard[] {
  const iteAuthorized = answers.urbanisme_ite_6;
  const itiFeasibility = answers.faisabilite_iti_7;
  const wallType = answers.nature_murs;

  const iteImpossible = iteAuthorized === 'ite_interdite' || iteAuthorized === 'forbidden';
  const itiDifficult = itiFeasibility === 'iti_difficile' || itiFeasibility === 'difficult';

  if (iteImpossible && itiDifficult) {
    console.log('🚫 [WALL INSULATION FILTER] Both ITE and ITI impossible, returning empty list');
    return [];
  }

  console.log('🔍 [WALL INSULATION FILTER] Starting filter with:', {
    iteImpossible,
    itiDifficult,
    wallType,
    totalEquipments: equipments.length
  });

  let filtered = equipments.filter(equipment => {
    const equipmentExtended = equipment as EquipmentStandard & {
      insulation_location?: string;
      is_breathable?: boolean | null;
    };
    const location = equipmentExtended.insulation_location;

    if (iteImpossible && location === 'exterior') {
      console.log(`🚫 [WALL INSULATION FILTER] Filtering out ${equipment.name} (ITE impossible)`);
      return false;
    }

    if (itiDifficult && location === 'interior') {
      console.log(`🚫 [WALL INSULATION FILTER] Filtering out ${equipment.name} (ITI difficult)`);
      return false;
    }

    return true;
  });

  const needsBreathable = requiresBreathableMaterial(wallType);

  if (needsBreathable) {
    filtered = filtered.filter(equipment => {
      const equipmentExtended = equipment as EquipmentStandard & { is_breathable?: boolean | null };
      const isBreathable = equipmentExtended.is_breathable === true;

      if (!isBreathable && equipmentExtended.is_breathable !== null) {
        console.log(`🚫 [BREATHABLE FILTER] Filtering out ${equipment.name} (non-breathable material, wall requires breathable)`);
        return false;
      }

      return true;
    });
  }

  console.log(`✅ [WALL INSULATION FILTER] Filtered from ${equipments.length} to ${filtered.length} equipments`);

  if (filtered.length === 0 && needsBreathable) {
    console.log('⚠️ [WALL INSULATION FILTER] No equipment left after filtering, returning all breathable options from source list');
    const allBreathable = equipments.filter(eq => {
      const ext = eq as EquipmentStandard & { is_breathable?: boolean | null };
      return ext.is_breathable === true;
    });

    if (allBreathable.length > 0) {
      return allBreathable;
    }
  }

  return filtered;
}

export function filterRoofInsulationEquipments(
  equipments: EquipmentStandard[],
  dpe: NormalizedDPEData,
  answers: UserAnswers
): EquipmentStandard[] {
  const isTopFloorCombles = answers.dernier_etage === 'dernier_etage_sous_combles';
  const isTopFloorFlatRoof = answers.dernier_etage === 'dernier_etage_sous_toit_terrasse';
  const isHouseComblesPerdus = answers.type_toiture_maison === 'combles_perdus';
  const isHouseComblesAmenages = answers.type_toiture_maison === 'combles_amenages';
  const isHouseFlatRoof = answers.type_toiture_maison === 'toit_terrasse';

  const isFlatRoof = isTopFloorFlatRoof || isHouseFlatRoof;
  const isInhabitedAttic = isTopFloorCombles || isHouseComblesAmenages;
  const isLostAttic = isHouseComblesPerdus;

  if (!isFlatRoof && !isInhabitedAttic && !isLostAttic) {
    return [];
  }

  return equipments.filter(equipment => {
    const equipmentExtended = equipment as EquipmentStandard & { insulation_location?: string };
    const location = equipmentExtended.insulation_location;

    if (isFlatRoof) {
      return location === 'flat_roof';
    }

    if (isInhabitedAttic) {
      return location === 'inhabited_attic';
    }

    if (isLostAttic) {
      return location === 'attic';
    }

    return true;
  });
}